import { useState } from "react";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import "open-iconic/font/css/open-iconic-bootstrap.css";
import {
  Grid,
  TableHeaderRow,
  TableTreeColumn,
  TableColumnResizing,
  Table,
} from "@devexpress/dx-react-grid-bootstrap4";
import {
  CustomTreeData,
  TreeDataState,
  IntegratedSorting,
  SortingState,
} from "@devexpress/dx-react-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  UncontrolledTooltip,
} from "reactstrap";
import { FormattedMessage } from "react-intl";

const TableComponent = ({ ...restProps }) => (
  <Table.Table {...restProps} className="table-striped result-table" />
);

export default function ResultTable(props) {
  const [showStatusInfoModal, setShowStatusInfoModal] = useState(false);
  const [showNbrOfAttemptsInfoModal, setNbrOfAttemptsInfoModal] =
    useState(false);
  const [showFocusControlInfoModal, setFocusControlInfoModal] = useState(false);

  let usersData;
  const rightAlignedCols = props.rightAlignedColumns || [];
  const centerAlignedCols = props.centerAlignedColumns || [];

  const getChildRows = (row, rootRows) => {
    const childRows = rootRows.filter(
      (r) => r.parentId === (row ? row.id : null)
    );
    return childRows.length ? childRows : null;
  };

  const [defaultColumnWidths] = useState(props.columnExtensions);

  const statusPriority = {
    InProgress: 1,
    NotStarted: 0,
    Done: 2,
    Completed: 3,
  };

  const comparePriority = (a, b) => {
    if (!a || !a.props) return -1;
    if (!b || !b.props) return 1;

    const priorityA = statusPriority[a.props.status] ?? -1;
    const priorityB = statusPriority[b.props.status] ?? -1;

    if (priorityA === priorityB) return 0;
    return priorityA < priorityB ? -1 : 1;
  };

  const getAlignmentClass = (columnName, rightCols, centerCols, type) => {
    if (rightCols.includes(columnName)) return `right-align-${type}`;
    if (centerCols.includes(columnName)) return `center-align-${type}`;
    return "";
  };

  const alignCell =
    (Component, type) =>
    ({ column, children, className, ...restProps }) => {
      const alignmentClass = getAlignmentClass(
        column.name,
        rightAlignedCols,
        centerAlignedCols,
        type
      );
      return (
        <Component
          {...restProps}
          className={`${className || ""} ${alignmentClass}`}
        >
          {children}
        </Component>
      );
    };

  const CustomCell = alignCell(Table.Cell, "cell");

  const CustomHeaderCell = (props) => {
    const { column, children, className, ...restProps } = props;

    const alignmentClass = getAlignmentClass(
      column.name,
      rightAlignedCols,
      centerAlignedCols,
      "header"
    );

    const isStatus = column.name === "status";
    const isAttempts = column.name === "nbrOfAttempts";
    const isFocusControl = column.name === "focusLostCount";

    if (isStatus || isAttempts || isFocusControl) {
      return (
        <TableHeaderRow.Cell
          {...props}
          className={`${className || ""} ${alignmentClass}`}
        >
          <div
            className={`d-flex w-100 ${
              isStatus ? "justify-content-center" : "justify-content-end"
            }`}
          >
            <div className="d-flex">
              {children}

              <FontAwesomeIcon
                id={
                  isStatus
                    ? "statusInfoIcon"
                    : isAttempts
                      ? "nbrOfAttemptsInfoIcon"
                      : "focusControlInfoIcon"
                }
                icon="info-circle"
                className={
                  (isStatus
                    ? "status-info-icon"
                    : isAttempts
                      ? "attempts-info-icon"
                      : "focus-control-info-icon") + " text-primary"
                }
                style={{ cursor: "pointer" }}
                onClick={() =>
                  isStatus
                    ? setShowStatusInfoModal(true)
                    : isAttempts
                      ? setNbrOfAttemptsInfoModal(true)
                      : setFocusControlInfoModal(true)
                }
              />

              {isAttempts && (
                <UncontrolledTooltip
                  placement="top"
                  target="nbrOfAttemptsInfoIcon"
                >
                  <FormattedMessage id="NbrOfAttempts_Info" />
                </UncontrolledTooltip>
              )}
            </div>
          </div>
        </TableHeaderRow.Cell>
      );
    }

    return (
      <TableHeaderRow.Cell
        {...restProps}
        className={`${className || ""} ${alignmentClass}`}
      >
        {children}
      </TableHeaderRow.Cell>
    );
  };

  const CustomTreeCell = ({ row, children, ...restProps }) => {
    if (row.isGy25FinalExam) {
      const indent = row.level ? row.level * 20 : 0; // 20px per level

      return (
        <TableTreeColumn.Cell
          className="result-row--gy25FinalExam"
          {...restProps}
          indentComponent={() => null}
          contentComponent={() => null}
        >
          <div
            style={{
              marginLeft: `${indent}px`,
              display: "flex",
              alignItems: "center",
            }}
          >
            <span>{row.name}</span>
            <span style={{ marginLeft: "-3em" }}>{children}</span>
          </div>
        </TableTreeColumn.Cell>
      );
    }
    return (
      <TableTreeColumn.Cell {...restProps}>{children}</TableTreeColumn.Cell>
    );
  };

  const comparePercentage = (a, b) => {
    if (!a) return -1;
    if (!b) return 1;

    const getPriority = (value) => {
      if (value === 0) return 0;
      const percentageArray = value.match(/\(\d+%\)/);
      if (!percentageArray) return Number(value);

      const percentage = percentageArray[0].replace(/[()%]/g, "");
      return Number(percentage);
    };

    const priorityA = getPriority(a);
    const priorityB = getPriority(b);

    if (priorityA === priorityB) return 0;
    return priorityA < priorityB ? -1 : 1;
  };

  const [integratedSortingColumnExtensions] = useState([
    { columnName: "status", compare: comparePriority },
    { columnName: "score", compare: comparePercentage },
    { columnName: "activityPoints", compare: comparePercentage },
  ]);
  const [sortingStateColumnExtensions] = useState([
    { columnName: "showResult", sortingEnabled: false },
    { columnName: "lastRun", sortingEnabled: false },
  ]);

  usersData = props.tableData;

  return (
    <>
      <Grid rows={usersData} columns={props.columns}>
        <TreeDataState />
        <CustomTreeData getChildRows={getChildRows} />
        <SortingState columnExtensions={sortingStateColumnExtensions} />
        <IntegratedSorting
          columnExtensions={integratedSortingColumnExtensions}
        />

        <Table tableComponent={TableComponent} cellComponent={CustomCell} />

        <TableColumnResizing defaultColumnWidths={defaultColumnWidths} />

        <TableHeaderRow showSortingControls cellComponent={CustomHeaderCell} />

        <TableTreeColumn for="name" cellComponent={CustomTreeCell} />
      </Grid>

      <Modal isOpen={showStatusInfoModal}>
        <ModalHeader className="justify-content-center py-2 px-2">
          <FormattedMessage id="StatusInfo_Title" />
        </ModalHeader>

        <ModalBody>
          <p>
            <FormattedMessage id="StatusInfo_Intro" />
          </p>

          <ul>
            <li>
              <FormattedMessage id="StatusInfo_LeftIcon" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_RightIcon" />
            </li>
          </ul>

          <p>
            <FormattedMessage id="StatusInfo_Observe" />
          </p>

          <p>
            <strong>
              <FormattedMessage id="StatusInfo_Statuses" />
            </strong>
          </p>

          <p>
            <strong>
              <FormattedMessage id="StatusInfo_NotStarted_Title" />
            </strong>
          </p>
          <ul>
            <li>
              <FormattedMessage id="StatusInfo_NotStarted_1" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_NotStarted_2" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_NotStarted_3" />
            </li>
          </ul>

          <p>
            <strong>
              <FormattedMessage id="StatusInfo_InProgress_Title" />
            </strong>
          </p>
          <ul>
            <li>
              <FormattedMessage id="StatusInfo_InProgress_1" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_InProgress_2" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_InProgress_3" />
            </li>
          </ul>

          <p>
            <strong>
              <FormattedMessage id="StatusInfo_Completed_Title" />
            </strong>
          </p>
          <ul>
            <li>
              <FormattedMessage id="StatusInfo_Completed_1" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_Completed_2" />
            </li>
            <li>
              <FormattedMessage id="StatusInfo_Completed_3" />
            </li>
          </ul>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            color="primary"
            outline
            onClick={() => setShowStatusInfoModal(false)}
          >
            <FormattedMessage id="Close" />
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showNbrOfAttemptsInfoModal} size="sm" centered>
        <ModalBody>
          <p className="mb-0 fw-normal" style={{ fontSize: "0.9rem" }}>
            <FormattedMessage id="NbrOfAttempts_Info" />
          </p>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            color="primary"
            outline
            size="sm"
            onClick={() => setNbrOfAttemptsInfoModal(false)}
          >
            <FormattedMessage id="Close" />
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showFocusControlInfoModal}
        toggle={() => setFocusControlInfoModal(false)}
        size="md"
        scrollable
      >
        <ModalHeader
          className="py-2 px-3"
          toggle={() => setFocusControlInfoModal(false)}
        >
          <FormattedMessage id="FocusControlInfo_Title" />
        </ModalHeader>

        <ModalBody className="px-3 py-3">
          <p className="text-muted mb-3">
            <FormattedMessage id="FocusControlInfo_Intro" />
          </p>

          <div className="bg-light rounded p-3 mb-3">
            <h6 className="fw-semibold mb-2">
              <FormattedMessage id="FocusControlInfo_Overview_Title" />
            </h6>
            <p className="mb-2">
              <FormattedMessage id="FocusControlInfo_Overview_Intro" />
            </p>
            <ul className="mb-2 ps-3">
              <li className="mb-1">
                <FormattedMessage id="FocusControlInfo_Overview_FocusLost" />
              </li>
              <li>
                <FormattedMessage id="FocusControlInfo_Overview_TotalTime" />
              </li>
            </ul>
            <p className="text-muted mb-0 small">
              <FormattedMessage id="FocusControlInfo_Overview_Outro" />
            </p>
          </div>

          <div className="bg-light rounded p-3">
            <h6 className="fw-semibold mb-2">
              <FormattedMessage id="FocusControlInfo_Detail_Title" />
            </h6>
            <p className="mb-2">
              <FormattedMessage id="FocusControlInfo_Detail_Intro" />
            </p>
            <p className="text-muted small mb-1">
              <FormattedMessage id="FocusControlInfo_Detail_Columns" />
            </p>
            <ul className="mb-2 ps-3">
              <li className="mb-1">
                <FormattedMessage id="FocusControlInfo_Detail_Attempt" />
                <span className="d-block text-muted small mt-1">
                  <FormattedMessage id="FocusControlInfo_Detail_Attempt_Extra" />
                </span>
              </li>
              <li className="mb-1">
                <FormattedMessage id="FocusControlInfo_Detail_Time" />
              </li>
              <li>
                <FormattedMessage id="FocusControlInfo_Detail_Duration" />
              </li>
            </ul>
            <p className="mb-0 text-muted small">
              <FormattedMessage id="FocusControlInfo_Detail_Outro" />
            </p>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            color="primary"
            outline
            onClick={() => setFocusControlInfoModal(false)}
          >
            <FormattedMessage id="Close" />
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
